import { FunctionComponent } from 'react'
import { Button, Form, Spinner } from 'react-bootstrap'
import Icon from 'components/ui/icon'

import DatePicker from 'components/forms/datePicker'
import { HospitalAvailable } from 'features/hospitals/types'
import { Doctor } from 'features/doctors/types'
import { ManagedHospital } from 'features/adminHospital/types'
import {
  CONVENIO_FILTER_ALL,
  CONVENIO_FILTER_CONVENIOS_OU_PARTICULAR,
  Convenio,
  ConvenioFilter,
} from '../types'
import transfersCss from '../styles.module.scss'

export const TRANSFER_FILTER_MIN_DATE = new Date(2025, 0, 1)

export type DateRange = { start: Date; end: Date }

type Select<T> = {
  options: T[]
  value: number
  onChange: (id: number) => void
}

type TransferFiltersProps = {
  /** Doctor: hospitals the doctor has access to. */
  hospitalSelect?: Select<HospitalAvailable>
  /** Hospital admin managing more than one hospital. */
  adminHospitalSelect?: Select<ManagedHospital>
  /** Hospital admin: doctors of the selected hospital. */
  doctorSelect?: Select<Doctor>
  dates: DateRange
  onDateChange: (event: React.ChangeEvent<HTMLInputElement>, value?: Date) => void
  atendimento: string | undefined
  onAtendimentoChange: (value: string) => void
  convenio?: { value: ConvenioFilter; onChange: (value: ConvenioFilter) => void }
  loading: boolean
  onSearch: () => void
  syncLabel: string | null
}

const parseId = (event: React.ChangeEvent<HTMLSelectElement>) =>
  Number.parseInt(event.currentTarget.value, 10)

const FilterField: FunctionComponent<{
  label: string
  htmlFor?: string
  className?: string
  children: React.ReactNode
}> = ({ label, htmlFor, className, children }) => (
  <div className={className}>
    <label className={transfersCss.filterLabel} htmlFor={htmlFor}>
      {label}
    </label>
    {children}
  </div>
)

const TransferFilters: FunctionComponent<TransferFiltersProps> = ({
  hospitalSelect,
  adminHospitalSelect,
  doctorSelect,
  dates,
  onDateChange,
  atendimento,
  onAtendimentoChange,
  convenio,
  loading,
  onSearch,
  syncLabel,
}) => {
  const datesDisabled = Boolean(atendimento)

  return (
    <section className={`card ${transfersCss.filters}`}>
      <Form
        className={transfersCss.filterRow}
        onSubmit={(event) => {
          event.preventDefault()
          onSearch()
        }}
      >
        {hospitalSelect && (
          <FilterField
            label="Hospital"
            htmlFor="filter-hospital"
            className={transfersCss.filterWide}
          >
            <Form.Select
              id="filter-hospital"
              value={hospitalSelect.value}
              onChange={(event) => hospitalSelect.onChange(parseId(event))}
            >
              {hospitalSelect.options.map((hospital) => (
                <option key={hospital.id} value={hospital.id}>
                  {hospital.name}
                </option>
              ))}
            </Form.Select>
          </FilterField>
        )}

        {adminHospitalSelect && (
          <FilterField
            label="Hospital"
            htmlFor="filter-admin-hospital"
            className={transfersCss.filterWide}
          >
            <Form.Select
              id="filter-admin-hospital"
              value={adminHospitalSelect.value}
              onChange={(event) => adminHospitalSelect.onChange(parseId(event))}
            >
              {adminHospitalSelect.options.map((hospital) => (
                <option key={hospital.hospitalId} value={hospital.hospitalId}>
                  {hospital.hospital}
                </option>
              ))}
            </Form.Select>
          </FilterField>
        )}

        {doctorSelect && (
          <FilterField
            label="Médico"
            htmlFor="filter-doctor"
            className={transfersCss.filterWide}
          >
            <Form.Select
              id="filter-doctor"
              value={doctorSelect.value}
              onChange={(event) => doctorSelect.onChange(parseId(event))}
            >
              {doctorSelect.options.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.name}
                </option>
              ))}
            </Form.Select>
          </FilterField>
        )}

        <FilterField label="Período" className={transfersCss.filterPeriod}>
          <div className={transfersCss.dateRange}>
            <DatePicker
              name="start"
              value={dates.start}
              onChange={onDateChange}
              placeholder="Data inicial"
              disabled={datesDisabled}
              minDate={TRANSFER_FILTER_MIN_DATE}
            />
            <span className={transfersCss.dateSeparator}>–</span>
            <DatePicker
              name="end"
              value={dates.end}
              onChange={onDateChange}
              placeholder="Data final"
              disabled={datesDisabled}
              minDate={TRANSFER_FILTER_MIN_DATE}
            />
          </div>
        </FilterField>

        <FilterField
          label="Atendimento"
          htmlFor="filter-atendimento"
          className={transfersCss.filterAtendimento}
        >
          <Form.Control
            id="filter-atendimento"
            type="number"
            placeholder="Nº"
            value={atendimento ?? ''}
            onChange={(event) => onAtendimentoChange(event.currentTarget.value)}
          />
        </FilterField>

        {convenio && (
          <FilterField
            label="Convênio"
            htmlFor="filter-convenio"
            className={transfersCss.filterConvenio}
          >
            <Form.Select
              id="filter-convenio"
              value={convenio.value}
              onChange={(event) =>
                convenio.onChange(event.currentTarget.value as ConvenioFilter)
              }
            >
              <option value={CONVENIO_FILTER_ALL}>Todos</option>
              <option value={Convenio.SUS}>SUS</option>
              <option value={CONVENIO_FILTER_CONVENIOS_OU_PARTICULAR}>
                Convênios ou Particular
              </option>
            </Form.Select>
          </FilterField>
        )}

        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <Icon name="search" />
          )}
          {loading ? 'Buscando…' : 'Buscar'}
        </Button>
      </Form>

      {syncLabel && (
        <p className={transfersCss.syncInfo}>
          Dados sincronizados com o hospital em {syncLabel}
        </p>
      )}
    </section>
  )
}

export default TransferFilters
